import useMediaQuery from '@mui/material/useMediaQuery';
import type { GridDataSource } from '@mui/x-data-grid-pro';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { Asset } from '../../../api/models/asset.model.ts';
import type { NonLeasedAsset } from '../../../api/models/nonLeasedAsset.model.ts';
import type { InvoiceAsset } from '../../../api/models/invoiceAsset.model';
import DataGridProComponent from '../../../components/DataGrid/DataGridProComponent';
import {
	AssetsDataGridType,
	NonLeasedAssetsDataGridType,
	InvoiceAssetsDataGridType,
} from '../../../components/DataGrid/DataGridTypes.ts';
import {
	assetLocationColumns,
	assetSpecsColumns,
	customerAttributesColumns,
	leaseDetailsColumns,
	mobileNonLeasedAssetColumns,
	nonLeasedAssetCustomerAttributesColumns,
	nonLeasedAssetLeaseDetailsColumns,
	nonLeasedAssetLocationColumns,
	nonLeasedAssetSpecsColumns,
	invoiceAssetColumnGroups,
} from '../../../components/DataGrid/columnDefinitions';
import { mobileAssetColumns } from '../../../components/DataGrid/columnDefinitions.tsx';
import AssetDetailModal from '../../../components/Modals/AssetDetailModal/AssetDetailModal.tsx';
import AuditLog from '../../../components/Modals/AuditLog/AuditLog.tsx';
import { INVOICE_ASSET_TYPE, LEASED_ASSET_TYPE, NON_LEASED_ASSET_TYPE } from '../../../constants/logic.ts';
import { ASSET_NUMBER, setTableMessage } from '../../../constants/messages.tsx';
import { clearSelectedAssetSerialNo, selectSelectedAssetSerialNo } from '../../../redux/slices/assetSlice';
import { selectAssetView, setAssetView } from '../../../redux/slices/uiSettingsSlice';
import { selectAccount } from '../../../redux/slices/accountSlice.ts';
import { useGetAssetUserDefinedFieldsQuery } from '../../../api/asset.api.ts';
import type { SECTION_CHANGE } from '../../../components/DataGrid/TableViewSelector/TableViewSelector.tsx';

interface AssetsProps {
	assetsDataSource: GridDataSource;
	assetData: Asset[] | NonLeasedAsset[] | InvoiceAsset[];
	dataIsLoading: boolean;
	assetType?: typeof LEASED_ASSET_TYPE | typeof NON_LEASED_ASSET_TYPE | 'invoiceAsset';
	onRefresh?: () => void;
	refreshTrigger?: number;
}

const Assets: React.FC<AssetsProps> = ({
	assetsDataSource,
	assetData,
	dataIsLoading,
	assetType = LEASED_ASSET_TYPE,
	onRefresh,
	refreshTrigger,
}) => {
	const dispatch = useDispatch();
	const assetView = useSelector(selectAssetView);
	const selectedAssetSerialNo = useSelector(selectSelectedAssetSerialNo);
	const account = useSelector(selectAccount);
	const { data: userDefinedFieldDefinitions } = useGetAssetUserDefinedFieldsQuery({ accountId: account.accountId });
	const [assetDetailModalOpen, setAssetDetailModalOpen] = useState(false);
	const [auditLogModalOpen, setAuditLogModalOpen] = useState(false);
	const [selectedAsset, setSelectedAsset] = useState<any | null>(null);
	const [customerAttributeSection, setCustomerAttributeSection] = useState<number>(0);
	const [customerAttributesPerSection, setCustomerAttributesPerSection] = useState<Record<number, Record<string, string>>>();
	const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('md'));

	const isPurchasePriceRestricted = useMemo(() => {
		if (assetType !== LEASED_ASSET_TYPE || assetData.length === 0) return false;
		return (assetData as any[]).every((a) => a.purchasePrice == null);
	}, [assetData, assetType]);

	// Set default assetView to 'assetDetails' for INVOICE_ASSET_TYPE on mount
	useEffect(() => {
		if (assetType === INVOICE_ASSET_TYPE) {
			dispatch(setAssetView('assetDetails'));
		}
	}, [assetType, dispatch]);

	useEffect(() => {
		const numberOfAttributesPerSection = 3;
		if (userDefinedFieldDefinitions) {
			const sections: Record<number, Record<string, string>> = {};

			// Get all UDF entries (filtering out undefined values)
			const udfEntries = Object.entries(userDefinedFieldDefinitions).filter(
				([_, value]) => value !== undefined && value !== null
			) as [string, string][];

			// Divide UDFs into sections
			udfEntries.forEach(([key, value], index) => {
				const sectionIndex = Math.floor(index / numberOfAttributesPerSection);

				if (!sections[sectionIndex]) {
					sections[sectionIndex] = {};
				}

				sections[sectionIndex][key] = value;
			});

			setCustomerAttributesPerSection(sections);
		}
	}, [userDefinedFieldDefinitions]);

	const onSectionChange = (action: SECTION_CHANGE) => {
		if (action === 'NEXT' && customerAttributesPerSection && customerAttributeSection < Object.keys(customerAttributesPerSection).length - 1) {
			setCustomerAttributeSection((prev) => prev + 1);
		}
		if (action === 'PREVIOUS' && customerAttributeSection > 0) {
			setCustomerAttributeSection((prev) => prev - 1);
		}
	}

	// Return columns based on asset type and view
	const getAssetColumns = (view: string) => {
		if (assetType === INVOICE_ASSET_TYPE) {
			switch (view) {
				case 'customerAttributes':
					return invoiceAssetColumnGroups.customerAttributes(customerAttributesPerSection, customerAttributeSection);
				case 'assetLocation':
					return invoiceAssetColumnGroups.assetLocation;
				case 'billingLocation':
					return invoiceAssetColumnGroups.billingLocation;
				case 'invoiceDetails':
					return invoiceAssetColumnGroups.invoiceDetails;
				case 'assetDetails':
				case 'assetSpecs':
				default:
					return invoiceAssetColumnGroups.assetDetails;
			}
		}
		if (assetType === NON_LEASED_ASSET_TYPE) {
			switch (view) {
				case 'customerAttributes':
					return nonLeasedAssetCustomerAttributesColumns(customerAttributesPerSection, customerAttributeSection);
				case 'assetLocation':
					return nonLeasedAssetLocationColumns;
				case 'leaseDetails':
					return nonLeasedAssetLeaseDetailsColumns;
				case 'assetSpecs':
				default:
					return nonLeasedAssetSpecsColumns;
			}
		}
		switch (view) {
			case 'customerAttributes':
				return customerAttributesColumns(customerAttributesPerSection, customerAttributeSection);
			case 'assetLocation':
				return assetLocationColumns;
			case 'leaseDetails':
				return isPurchasePriceRestricted
					? leaseDetailsColumns.filter((col) => col.field !== 'purchasePrice')
					: leaseDetailsColumns;
			case 'assetSpecs':
			default:
				return assetSpecsColumns;
		}
	};

	const getMobileColumns = () => {
		if (assetType === INVOICE_ASSET_TYPE) {
			return mobileAssetColumns;
		}
		if (assetType === NON_LEASED_ASSET_TYPE) {
			return mobileNonLeasedAssetColumns;
		}
		return mobileAssetColumns;
	};

	// Get columns based on the current view and asset type
	const getColumnsForView = () => {
		return isMobile ? getMobileColumns() : getAssetColumns(assetView);
	};

	const getDataGridType = () => {
		if (assetType === INVOICE_ASSET_TYPE) {
			return InvoiceAssetsDataGridType;
		}
		return assetType === NON_LEASED_ASSET_TYPE ? NonLeasedAssetsDataGridType : AssetsDataGridType;
	};

	const handleAssetClick = (asset: any) => {
		setSelectedAsset(asset);
		setAssetDetailModalOpen(true);
	};

	React.useEffect(() => {
		if (selectedAssetSerialNo) {
			const foundAsset = assetData.find((a) => {
				// For Asset/NonLeasedAsset, use serialNumber; for InvoiceAsset, use assetNumber
				if ('serialNumber' in a && a.serialNumber !== undefined) {
					return a.serialNumber === selectedAssetSerialNo;
				}
				if ('assetNumber' in a && a.assetNumber !== undefined) {
					return a.assetNumber === selectedAssetSerialNo;
				}
				return false;
			});
			if (foundAsset) {
				handleAssetClick(foundAsset);
			}
		}
	}, [selectedAssetSerialNo, assetData]);

	const handleModalClose = () => {
		setAssetDetailModalOpen(false);
		setAuditLogModalOpen(false);
		dispatch(clearSelectedAssetSerialNo());
	};

	const handleAuditLogOpen = () => {
		setAssetDetailModalOpen(false);
		setAuditLogModalOpen(true);
	};

	const handleAuditLogClose = () => {
		setAssetDetailModalOpen(true);
		setAuditLogModalOpen(false);
	};

	const getSectionPosition =
		(customerAttributesPerSection: Record<number, Record<string, string>>, customerAttributesSection: number):
			number | undefined => {
			if (!customerAttributesPerSection) return undefined;
			if (customerAttributesSection === 0) return -1;
			if (customerAttributesSection === Object.keys(customerAttributesPerSection).length - 1) return 1;
			return 0;
		}

	const shouldShowSectionChange = (
		assetView: string,
		customerAttributesPerSection: Record<number, Record<string, string>> | undefined) => {
		return assetView === 'customerAttributes' && customerAttributesPerSection && Object.keys(customerAttributesPerSection).length > 1;
	}

	return (
		<>
			{setTableMessage(ASSET_NUMBER)}
			<DataGridProComponent
				columns={getColumnsForView()}
				dataGridType={getDataGridType()}
				dataSource={assetsDataSource}
				dataIsLoading={dataIsLoading}
				onRefresh={onRefresh}
				onSectionChange={shouldShowSectionChange(assetView, customerAttributesPerSection) ? onSectionChange : undefined}
				sectionPosition={getSectionPosition(customerAttributesPerSection!, customerAttributeSection)}
				refreshTrigger={refreshTrigger}
			/>
			{selectedAsset && (
				<AssetDetailModal
					open={assetDetailModalOpen}
					onClose={handleModalClose}
					asset={selectedAsset}
					onAuditLogClick={handleAuditLogOpen}
					isNonLeasedAsset={assetType === NON_LEASED_ASSET_TYPE}
				/>
			)}
			{selectedAsset && (
				<AuditLog
					onClose={handleAuditLogClose}
					open={auditLogModalOpen}
					asset={selectedAsset}
					backToAssetDetails={handleAuditLogClose}
					backToAssetList={handleModalClose}
				/>
			)}
		</>
	);
};

export default Assets;
